<template>
  <div class="booster-ccs-period">
    <div class="period-header">
      <div class="period-header__text">
        <h5 class="period-title mb-0">{{ periodTitle }}</h5>
        <p v-if="showExplore" class="period-subtitle mb-0">
          {{ $t("widgets.boosterCcs.exploreResults") }}
        </p>
      </div>
      <button
        v-if="period?.campaign_url"
        type="button"
        class="action-btn fw-bold bg-white text-uppercase"
        @click="openMechanics"
      >
        {{ $t("widgets.boosterCcs.learnMechanics") }}
      </button>
    </div>

    <section class="period-grid">
      <div class="col metric-col with-separator">
        <p class="section-label">{{ $t("widgets.boosterCcs.yourRealisation") }}</p>
        <div class="metric-icon" aria-hidden="true">
          <img src="@/assets/img/points_board/std-rim.png" alt="" />
        </div>
        <div class="metric-value text-success">
          {{ $filters.formatNumberWithCommas(tyresSold) }}
        </div>
        <p class="metric-caption">{{ $t("widgets.boosterCcs.soldTyres") }}</p>
        <p class="metric-note">{{ $t("widgets.boosterCcs.tyreLimitInfo") }}</p>
      </div>

      <div class="col metric-col with-separator">
        <p class="section-label">{{ $t("widgets.boosterCcs.yourPoints") }}</p>
        <p class="section-hint">{{ $t("widgets.boosterCcs.pointsBySales") }}</p>
        <div class="metric-icon" aria-hidden="true">
          <img src="@/assets/img/points_board/reward-icon.png" alt="" />
        </div>
        <div class="metric-value text-success">
          {{ $filters.formatNumberWithCommas(pointsEarned) }}
        </div>
        <p class="metric-caption">{{ $t("widgets.boosterCcs.receivedPoints") }}</p>
      </div>

      <div class="col charts-col">
        <p class="section-label">{{ $t("widgets.boosterCcs.salesDetail") }}</p>

        <div v-if="hasAnyChart" class="charts-row">
          <div v-if="hasBrandChart" class="chart-block">
            <PieChart
              :data="period.by_brand"
              :centerText="$t('widgets.boosterCcs.charts.byBrand')"
            />
          </div>
          <div
            v-if="hasBrandChart && hasSizeChart"
            class="charts-separator"
            aria-hidden="true"
          />
          <div v-if="hasSizeChart" class="chart-block">
            <PieChart
              :data="period.by_size"
              :centerText="$t('widgets.boosterCcs.charts.bySize')"
              legendPosition="down"
              :reduceColors="true"
            />
          </div>
        </div>

        <p v-else class="charts-empty mb-0">
          {{ $t("widgets.boosterCcs.noSalesBreakdown") }}
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import PieChart from "@/components/Shared/PieChart.vue";

const props = defineProps({
  period: { type: Object, required: true },
  showExplore: { type: Boolean, default: true },
});

const sumValues = (data) => {
  if (!data || typeof data !== "object") return 0;
  return Object.values(data).reduce((sum, value) => sum + (Number(value) || 0), 0);
};

const periodTitle = computed(() => props.period?.title || "");
const tyresSold = computed(() => Number(props.period?.tyres_sold ?? 0));
const pointsEarned = computed(() => Number(props.period?.points ?? 0));
const hasBrandChart = computed(() => sumValues(props.period?.by_brand) > 0);
const hasSizeChart = computed(() => sumValues(props.period?.by_size) > 0);
const hasAnyChart = computed(() => hasBrandChart.value || hasSizeChart.value);

const openMechanics = () => {
  if (props.period?.campaign_url) {
    window.location.href = props.period.campaign_url;
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";

.period-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.period-title {
  font-size: 18px;
  font-weight: 700;
}

.period-subtitle {
  margin-top: 4px;
  font-size: 14px;
  color: #6c6c6c;
}

.action-btn {
  height: 40px;
  border: 1px solid #b3b3b3;
  border-radius: 25px;
  padding: 5px 20px;
  font-size: 12px;
  white-space: nowrap;
}

.period-grid {
  display: flex;
  gap: 0;
}

.col {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 1.25rem;
}

.col:first-child {
  padding-left: 0;
}

.col:last-child {
  padding-right: 0;
}

.with-separator {
  position: relative;
}

.with-separator::after {
  content: "";
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #b3b3b3;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
}

.section-hint {
  font-size: 14px;
  margin-bottom: 1rem;
  color: #333;
}

.metric-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0 0.5rem;

  img {
    max-width: 88px;
    max-height: 88px;
    width: auto;
    height: auto;
    object-fit: contain;
  }
}

.metric-value {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  margin: 0.5rem 0 0.25rem;
}

.metric-caption {
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
}

.metric-note {
  font-size: 12px;
  line-height: 1.4;
  color: #666;
  margin: 0;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e5e5;
}

.charts-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.chart-block {
  flex: 1 1 0;
  min-width: 0;
}

.charts-separator {
  width: 1px;
  align-self: stretch;
  background-color: #b3b3b3;
  flex: 0 0 1px;
}

.charts-empty {
  font-size: 14px;
  color: #6c6c6c;
  padding: 1.5rem 0;
}

@media (max-width: 991px) {
  .period-grid {
    flex-direction: column;
    gap: 1.5rem;
  }

  .col {
    padding: 0;
  }

  .with-separator::after {
    display: none;
  }

  .metric-note {
    border-top: none;
    padding-top: 0;
  }

  .charts-row {
    flex-direction: column;
  }

  .charts-separator {
    width: 100%;
    height: 1px;
  }
}
</style>
